import React, { createContext, useContext, useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

const NavigateContext = createContext();

export const NavigateProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const prevPathRef = useRef(location.pathname);
    const [transitionClass, setTransitionClass] = useState('fade');

    // Add a useEffect hook to reset the transition class after navigation
    useEffect(() => {
        // Reset the transition class to 'fade' after the navigation has occurred
        const timeout = setTimeout(() => {
            setTransitionClass('fade');
        }, 300);  // Adjust the time to match your transition duration

        return () => clearTimeout(timeout);
    }, [location.pathname]);  // Trigger reset when the location changes

    const customNavigate = useCallback((to, options = {}) => {
        const from = prevPathRef.current;
        const toPath = to;

        // Check for transition conditions
        const isBoothListToDetail = from === '/booths' && /^\/booths\/[^/]+$/.test(toPath);
        const isBoothDetailToList = /^\/booths\/[^/]+$/.test(from) && toPath === '/booths';
        const isTransitionFromBoothsToOther = from === '/booths' && !/^\/booths\/[^/]+$/.test(toPath);

        // Set the appropriate transition class
        if (isBoothListToDetail) {
            setTransitionClass('slide-push');
        } else if (isBoothDetailToList) {
            setTransitionClass('slide-pop');
        } else if (isTransitionFromBoothsToOther) {
            setTransitionClass('fade');
        } else {
            setTransitionClass('fade');
        }

        // Navigate to the target route
        navigate(toPath, { ...options });

        // Update the prevPathRef with the current path after navigation
        prevPathRef.current = toPath;

        // Optionally log the transition for debugging
        console.log(`Navigating from ${from} to ${toPath} with transition: ${transitionClass}`);
    }, [navigate, transitionClass]);  // Include transitionClass to ensure it updates correctly

    return (
        <NavigateContext.Provider value={{ customNavigate, transitionClass }}>
            <TransitionGroup component={null}>
                <CSSTransition
                    key={location.pathname}
                    classNames={transitionClass}
                    timeout={300}
                    unmountOnExit
                >
                    <div className="route-wrapper">
                        {children}
                    </div>
                </CSSTransition>
            </TransitionGroup>
        </NavigateContext.Provider>
    );
};

export const useCustomNavigate = () => useContext(NavigateContext);
